import React, { useState } from 'react';
import type { PiV2Alert, PiV2AlertSeverity } from '../../../patient-insurance/typesV2';
import { Icon } from '../../Icon/Icon';
import type { IconKind } from '../../Icon/Icon';

const SEVERITY: Record<PiV2AlertSeverity, { fg: string; bg: string; icon: IconKind; label: string }> =
  {
    critical: { fg: 'var(--terracotta)', bg: 'var(--terracotta-tint)', icon: 'alert', label: 'Critical' },
    warning: { fg: 'var(--gold)', bg: 'var(--gold-tint)', icon: 'alert', label: 'Warning' },
    info: { fg: 'var(--ai-3)', bg: 'var(--ai-tint)', icon: 'info', label: 'Informational' },
  };

export interface PatientInsuranceAlertsProps {
  alerts: PiV2Alert[];
}

/**
 * ELVA Insurance Alerts strip (spec §9) — risks / blockers / missing data, kept
 * separate from Opportunities (which describe beneficial actions). At most three
 * are shown; the rest collapse behind "+N more". A subtle healthy state stands in
 * when nothing needs attention.
 */
export function PatientInsuranceAlerts({ alerts }: PatientInsuranceAlertsProps) {
  const [expanded, setExpanded] = useState(false);

  if (alerts.length === 0) {
    return (
      <div className="flex items-center gap-2 rounded-[var(--r-lg)] border border-[color-mix(in_srgb,var(--success)_22%,transparent)] bg-[var(--success-tint)] px-4 py-[11px] text-[13px] text-[var(--ink-1)]">
        <Icon
          kind="shield-check"
          size={15}
          stroke={2}
          style={{ color: 'var(--success)', flexShrink: 0 }}
        />
        <span>
          <b>Insurance looks good</b> · Active coverage · No major coverage issues detected
        </span>
      </div>
    );
  }

  const visible = expanded ? alerts : alerts.slice(0, 3);
  const hidden = alerts.length - visible.length;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="font-[var(--font-mono)] text-[9.5px] font-bold uppercase tracking-[0.12em] text-[var(--ink-4)]">
        ELVA insurance alerts
      </span>
      {visible.map((a) => {
        const s = SEVERITY[a.severity];
        return (
          <span
            key={a.id}
            className="inline-flex items-center gap-1.5 rounded-[var(--r-pill)] px-2.5 py-1 text-[12px] font-medium"
            style={{ background: s.bg, color: s.fg }}
          >
            <Icon kind={s.icon} size={12} stroke={2} aria-label={s.label} />
            {a.text}
          </span>
        );
      })}
      {hidden > 0 && !expanded ? (
        <button
          type="button"
          onClick={() => {
            setExpanded(true);
          }}
          className="rounded-[var(--r-pill)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-2.5 py-1 text-[12px] font-semibold text-[var(--ink-2)] hover:bg-[var(--cream-0)]"
        >
          +{hidden} more
        </button>
      ) : null}
    </div>
  );
}
